import { useState, useEffect } from "react";
import { Plus, Trash2, Gauge, CheckCircle2, Circle, Sparkles, RefreshCw, Zap } from "lucide-react";
import { useFinancialState } from "@/context/StateContext";
import { fmtDate, uid, fmtMoney, todayISO } from "@/lib/finance";
import { creditApi } from "@/lib/api";
import { PageHeader, SectionCard, EmptyState, AddButton, KpiCard, ProgressBar, StatusPill } from "@/components/shared";
import { FormModal, TextField, NumberField } from "@/components/forms";
import { TrendChart } from "@/components/Charts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

export default function Credit() {
  const { state, update, saveNow, loading } = useFinancialState();
  const [open, setOpen] = useState(false);
  const [snap, setSnap] = useState(null);
  const [actionText, setActionText] = useState("");
  const [plan, setPlan] = useState(null);
  const [planAt, setPlanAt] = useState(null);
  const [optimizing, setOptimizing] = useState(false);
  useEffect(() => {
    creditApi.latest().then((d) => { setPlan(d.plan); setPlanAt(d.created_at); }).catch(() => {});
  }, []);

  // Auto-calculate credit utilization from credit-card accounts in Accounts & debt
  useEffect(() => {
    if (!state) return;
    const cards = (state.accounts || []).filter((a) => a.type === "debt" && a.subtype === "credit_card" && Number(a.creditLimit) > 0);
    const limit = cards.reduce((s, a) => s + Number(a.creditLimit || 0), 0);
    const bal = cards.reduce((s, a) => s + Math.abs(Number(a.balance || 0)), 0);
    if (limit > 0) {
      const au = Math.round((bal / limit) * 100);
      if (au !== Number(state.credit?.utilization || 0)) {
        update((prev) => ({ ...prev, credit: { ...prev.credit, utilization: au } }), "Auto-updated utilization from cards");
      }
    }
  }, [state, update]);

  if (loading || !state) return <Skeleton className="h-96 rounded-[14px]" />;

  const credit = state.credit || { snapshots: [], actions: [], profile: {} };
  const snapshots = (credit.snapshots || []).map((s) => ({ label: fmtDate(s.date).replace(/,.*/, ""), value: s.score }));
  const creditCards = (state.accounts || []).filter((a) => a.type === "debt" && a.subtype === "credit_card" && Number(a.creditLimit) > 0);
  const ccBalance = creditCards.reduce((s, a) => s + Math.abs(Number(a.balance || 0)), 0);
  const ccLimit = creditCards.reduce((s, a) => s + Number(a.creditLimit || 0), 0);
  const autoUtil = ccLimit > 0 ? Math.round((ccBalance / ccLimit) * 100) : null;
  const util = autoUtil != null ? autoUtil : Number(credit.utilization || 0);
  const utilTone = util > 50 ? "danger" : util > 30 ? "gold" : "green";

  const setCredit = (patch, summary) => update((prev) => ({ ...prev, credit: { ...prev.credit, ...patch } }), summary);

  const addSnapshot = () => {
    if (!snap?.score) return toast.error("Score is required");
    const s = { date: snap.date || todayISO(), score: Number(snap.score) };
    setCredit({ score: s.score, snapshots: [...(credit.snapshots || []), s].sort((a, b) => a.date.localeCompare(b.date)) }, "Logged credit score");
    setOpen(false); toast.success("Credit score logged");
  };
  const addAction = () => {
    if (!actionText) return;
    setCredit({ actions: [...(credit.actions || []), { id: uid(), title: actionText, done: false }] }, "Added credit action");
    setActionText("");
  };
  const toggleAction = (id) => setCredit({ actions: credit.actions.map((a) => (a.id === id ? { ...a, done: !a.done } : a)) }, "Updated credit action");
  const removeAction = (id) => setCredit({ actions: credit.actions.filter((a) => a.id !== id) }, "Removed credit action");
  const setProfile = (k, v) => setCredit({ profile: { ...credit.profile, [k]: v } }, "Updated credit profile");

  // ---- Credit plan tracking ----
  const parsePoints = (s) => {
    const nums = String(s || "").match(/\d+/g);
    if (!nums || !nums.length) return 0;
    const vals = nums.map(Number);
    return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
  };
  const actionExists = (title) => (credit.actions || []).some((a) => a.title.trim().toLowerCase() === title.trim().toLowerCase());
  const addPlanAction = (a) => {
    if (actionExists(a.title)) { toast.info("Already in your checklist"); return; }
    setCredit({ actions: [...(credit.actions || []), { id: uid(), title: a.title, done: false, source: "ai", points: a.points || "", pointsVal: parsePoints(a.points), detail: a.detail, impact: a.impact }] }, "Saved AI action to checklist");
    toast.success("Added to your checklist");
  };
  const saveAllPlanActions = () => {
    const toAdd = (plan?.actions || []).filter((a) => !actionExists(a.title))
      .map((a) => ({ id: uid(), title: a.title, done: false, source: "ai", points: a.points || "", pointsVal: parsePoints(a.points), detail: a.detail, impact: a.impact }));
    if (!toAdd.length) { toast.info("All plan actions are already tracked"); return; }
    setCredit({ actions: [...(credit.actions || []), ...toAdd] }, "Saved AI plan to checklist");
    toast.success(`Added ${toAdd.length} action${toAdd.length === 1 ? "" : "s"} to your checklist`);
  };

  const realizedGains = (credit.actions || []).filter((a) => a.done && a.pointsVal).reduce((s, a) => s + Number(a.pointsVal || 0), 0);
  const potentialGains = (credit.actions || []).filter((a) => !a.done && a.pointsVal).reduce((s, a) => s + Number(a.pointsVal || 0), 0);
  const projectedFromPlan = (credit.score || 0) + potentialGains;

  const profileFields = [
    ["paymentHistory", "Payment history"], ["age", "Avg account age"],
    ["inquiries", "Hard inquiries"], ["mix", "Credit mix"], ["openAccounts", "Open accounts"],
  ];

  const optimize = async () => {
    setOptimizing(true);
    try {
      const p = await creditApi.optimize();
      setPlan(p); setPlanAt(new Date().toISOString());
      toast.success("AI credit plan ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Credit optimizer unavailable");
    } finally {
      setOptimizing(false);
    }
  };

  const impactTone = { high: "high", medium: "med", low: "low" };

  return (
    <div>
      <PageHeader title="Credit" subtitle="Monitor your score, utilization, and the actions that move it." testid="credit-header"
        actions={<><div className="flex items-center gap-2"><label className="text-xs text-[var(--text-muted)]">Target</label>
          <Input type="number" value={credit.target || 0} onChange={(e) => setCredit({ target: Number(e.target.value) }, "Updated target score")} className="w-24 bg-white border-[color:var(--border-subtle)]" data-testid="credit-target-input" /></div>
          <AddButton onClick={() => { setSnap({ date: todayISO(), score: "" }); setOpen(true); }} testid="log-score-button"><Plus className="h-4 w-4 mr-1.5" />Log score</AddButton></>} />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <KpiCard label="Current score" value={credit.score || 0} sub={`Target ${credit.target || "—"}`} icon={Gauge} accent testid="credit-score-kpi" />
        <KpiCard label="Utilization" value={`${util}%`} sub="Lower is better" testid="credit-util-kpi" />
        <KpiCard label="To target" value={Math.max(0, (credit.target || 0) - (credit.score || 0))} sub="Points to go" testid="credit-gap-kpi" />
        <KpiCard label="Actions open" value={(credit.actions || []).filter((a) => !a.done).length} sub="In progress" testid="credit-actions-kpi" />
        <div className="rounded-[14px] border border-[color:var(--border-subtle)] bg-white p-4" data-testid="credit-gains-kpi">
          <div className="flex items-center gap-1.5 text-[11px] text-[var(--text-muted)]"><Sparkles className="h-3.5 w-3.5 text-[#c68b3c]" />Score gains</div>
          <div className="font-serif-display text-2xl text-[var(--green)] tabular-nums mt-1" data-testid="credit-gains-value">
            +{realizedGains}<span className="text-xs text-[var(--text-faint)] font-normal"> of {realizedGains + potentialGains} pts</span>
          </div>
          <div className="mt-2"><ProgressBar value={realizedGains} max={Math.max(1, realizedGains + potentialGains)} tone="green" /></div>
          <div className="text-[11px] text-[var(--text-faint)] mt-1.5">{potentialGains} pts still to earn</div>
        </div>
      </div>

      {/* AI credit optimizer */}
      <SectionCard className="mb-4" testid="credit-ai-card"
        title="AI credit optimizer"
        subtitle={plan ? `Projected ${Math.round(plan.projected_score)} in ${plan.time_horizon}` : "Get a personalized, prioritized plan to raise your score"}
        right={<AddButton onClick={optimize} testid="optimize-credit-button">{optimizing ? <><RefreshCw className="h-4 w-4 mr-1.5 animate-spin" />Optimizing…</> : <><Sparkles className="h-4 w-4 mr-1.5" />{plan ? "Re-optimize" : "Optimize with AI"}</>}</AddButton>}>
        {optimizing ? <Skeleton className="h-40" /> : !plan ? (
          <EmptyState icon={Sparkles} title="No AI plan yet" message="Run the optimizer to get a step-by-step action plan tailored to your credit profile." />
        ) : (
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <div className="rounded-[10px] bg-[color:rgba(31,115,85,0.08)] px-3 py-2"><div className="text-[11px] text-[var(--text-muted)]">Projected score</div><div className="font-serif-display text-2xl text-[var(--green)] tabular-nums">{Math.round(plan.projected_score)}</div></div>
              <div className="rounded-[10px] bg-[var(--surface-3)] px-3 py-2"><div className="text-[11px] text-[var(--text-muted)]">Time horizon</div><div className="font-serif-display text-lg">{plan.time_horizon}</div></div>
              <div className="rounded-[10px] bg-[color:rgba(198,139,60,0.12)] px-3 py-2"><div className="text-[11px] text-[var(--text-muted)]">Utilization target</div><div className="text-sm font-medium text-[#8a5a17] mt-1">{plan.utilization_target}</div></div>
            </div>
            <p className="text-sm text-[var(--text-muted)] mb-4">{plan.summary}</p>
            <div className="space-y-2">
              {(plan.actions || []).map((a, i) => (
                <div key={i} className="rounded-[12px] border border-[color:var(--border-subtle)] bg-white p-3" data-testid={`credit-plan-action-${i}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2.5">
                      <span className="grid place-items-center h-5 w-5 rounded-full bg-[color:rgba(31,115,85,0.12)] text-[var(--green)] text-[11px] font-semibold shrink-0 mt-0.5">{i + 1}</span>
                      <div><div className="text-sm font-semibold text-[var(--text)]">{a.title}</div><p className="text-sm text-[var(--text-muted)] mt-0.5">{a.detail}</p></div>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <StatusPill status={impactTone[a.impact] || "low"} />
                      {a.points && <span className="text-[11px] text-[var(--green)] font-medium tabular-nums">{a.points}</span>}
                    </div>
                  </div>
                  <div className="mt-2 pl-7 flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5 text-[11px] text-[var(--text-faint)]"><Zap className="h-3 w-3" />{a.effort}</span>
                    <Button size="sm" variant="outline" disabled={actionExists(a.title)} onClick={() => addPlanAction(a)} data-testid={`add-plan-action-${i}`}
                      className="h-7 text-xs border-[color:var(--border-subtle)] disabled:opacity-70">
                      {actionExists(a.title) ? <><CheckCircle2 className="h-3 w-3 mr-1 text-[var(--green)]" />Added</> : <><Plus className="h-3 w-3 mr-1" />Add to checklist</>}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            {planAt && <p className="text-[11px] text-[var(--text-faint)] mt-3">Generated {fmtDate(planAt)}</p>}
          </div>
        )}
      </SectionCard>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-7 space-y-4">
          <SectionCard title="Score history" testid="credit-history-card">
            {snapshots.length ? <TrendChart data={snapshots} name="Score" color="#1f7355" /> : <EmptyState icon={Gauge} title="No scores logged" message="Log your credit score to track it over time." />}
          </SectionCard>
          <SectionCard title="Utilization" subtitle="Balance vs available credit" testid="credit-util-card">
            {autoUtil != null ? (
              <div data-testid="auto-util-block">
                <div className="flex items-baseline justify-between mb-2">
                  <div className="font-serif-display text-3xl text-[var(--text)] tabular-nums" data-testid="auto-util-value">{autoUtil}%</div>
                  <div className="text-xs text-[var(--text-muted)] tabular-nums">{fmtMoney(ccBalance)} / {fmtMoney(ccLimit)}</div>
                </div>
                <ProgressBar value={ccBalance} max={ccLimit} tone={utilTone} />
                <div className="mt-3 space-y-1.5">
                  {creditCards.map((c) => {
                    const u = Math.round((Math.abs(Number(c.balance || 0)) / Number(c.creditLimit)) * 100);
                    return (
                      <div key={c.id} className="flex items-center justify-between text-[11px]" data-testid={`util-card-${c.id}`}>
                        <span className="text-[var(--text-muted)] truncate">{c.name}</span>
                        <span className="tabular-nums text-[var(--text-faint)] shrink-0 ml-2">{fmtMoney(Math.abs(c.balance))} / {fmtMoney(c.creditLimit)} · {u}%</span>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[11px] text-[var(--text-faint)] mt-3 flex items-center gap-1"><Sparkles className="h-3 w-3" />Auto-calculated from {creditCards.length} credit card{creditCards.length === 1 ? "" : "s"} in Accounts &amp; debt. Keep it under 30% (ideally under 10%).</p>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-2">
                  <Input type="number" value={credit.utilization || 0} onChange={(e) => setCredit({ utilization: Number(e.target.value) }, "Updated utilization")} className="w-24 bg-white border-[color:var(--border-subtle)]" data-testid="util-input" />
                  <span className="text-sm text-[var(--text-muted)]">% used</span>
                </div>
                <ProgressBar value={util} max={100} tone={utilTone} />
                <p className="text-[11px] text-[var(--text-faint)] mt-2">Add credit cards with a limit in <span className="font-medium">Accounts &amp; debt</span> to auto-calculate this. Keep utilization under 30% (ideally under 10%).</p>
              </>
            )}
          </SectionCard>
        </div>
        <div className="lg:col-span-5 space-y-4">
          <SectionCard title="Credit actions" testid="credit-actions-card">
            <div className="flex gap-2 mb-3">
              <Input value={actionText} onChange={(e) => setActionText(e.target.value)} placeholder="Add an action…" className="bg-white border-[color:var(--border-subtle)]" data-testid="credit-action-input"
                onKeyDown={(e) => e.key === "Enter" && addAction()} />
              <Button onClick={addAction} className="bg-[var(--green)] text-[#f7fbf9]" data-testid="add-action-button"><Plus className="h-4 w-4" /></Button>
            </div>
            <div className="space-y-2">
              {(credit.actions || []).map((a) => (
                <div key={a.id} className="flex items-center justify-between rounded-[10px] border border-[color:var(--border-subtle)] bg-white px-3 py-2" data-testid={`action-row-${a.id}`}>
                  <button onClick={() => toggleAction(a.id)} className="flex items-center gap-2 text-left min-w-0" aria-label="Toggle">
                    {a.done ? <CheckCircle2 className="h-4 w-4 text-[var(--green)] shrink-0" /> : <Circle className="h-4 w-4 text-[var(--text-faint)] shrink-0" />}
                    <span className={`text-sm truncate ${a.done ? "line-through text-[var(--text-faint)]" : "text-[var(--text)]"}`}>{a.title}</span>
                    {a.source === "ai" && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-[color:rgba(198,139,60,0.14)] px-1.5 py-0.5 text-[10px] text-[#8a5a17] shrink-0" data-testid={`action-ai-badge-${a.id}`}>
                        <Sparkles className="h-2.5 w-2.5" />AI{a.points ? ` · ${a.points}` : ""}
                      </span>
                    )}
                  </button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-[var(--danger)]" onClick={() => removeAction(a.id)} aria-label="Delete"><Trash2 className="h-3 w-3" /></Button>
                </div>
              ))}
              {!(credit.actions || []).length && <p className="text-sm text-[var(--text-muted)] text-center py-4">No actions yet.</p>}
            </div>
          </SectionCard>
          <SectionCard title="Credit profile" subtitle="Details that sharpen your AI plan" testid="credit-profile-card">
            <div className="space-y-3">
              {profileFields.map(([k, label]) => (
                <div key={k} className="flex items-center justify-between gap-3">
                  <label className="text-sm text-[var(--text-muted)]">{label}</label>
                  <Input value={credit.profile?.[k] ?? ""} onChange={(e) => setProfile(k, e.target.value)} className="w-40 bg-white border-[color:var(--border-subtle)] text-sm" data-testid={`profile-${k}`} />
                </div>
              ))}
            </div>
            <Button onClick={() => { saveNow("Saved credit profile"); toast.success("Credit profile saved"); }}
              className="w-full mt-4 bg-[var(--green)] text-[#f7fbf9] hover:bg-[color:rgba(31,115,85,0.9)]" data-testid="save-profile-button">
              Save profile
            </Button>
          </SectionCard>
        </div>
      </div>

      {snap && (
        <FormModal open={open} onOpenChange={setOpen} title="Log credit score" onSubmit={addSnapshot} testid="score-modal">
          <NumberField label="Score" value={snap.score} onChange={(e) => setSnap({ ...snap, score: e.target.value })} testid="score-input" />
          <TextField label="Date" type="date" value={snap.date} onChange={(e) => setSnap({ ...snap, date: e.target.value })} />
        </FormModal>
      )}
    </div>
  );
}
